import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Dropdown from "shared/@forms/Dropdown";
import ImageRadioOption from "shared/ImageRadioOption";
import sanitizeSku from "DreamCast/Shared/sanitizeSku";
import formatDate, {
  formatReleaseDateInput,
  convertFormattedDateToISO,
  formatISODatetimeToDisplay,
} from "shared/@utils/formatDate";
import styles from "./AudioDetailsForm.module.css";

const DEFAULT_MEDIA_OPTIONS = ["Audio", "Video", "Music"];
const DEFAULT_CONTENT_TYPE_OPTIONS = ["Event", "Bio", "Music"];
const DEFAULT_CONTENT_TYPE_TEMPLATES = [
  {
    label: "Event",
    titleLabel: "Title",
    subtitleLabel: "Speaker",
    captionLabel: "Event",
  },
  {
    label: "Bio",
    titleLabel: "Title",
    subtitleLabel: "Speaker",
    captionLabel: "Occupation",
  },
  {
    label: "Music",
    titleLabel: "Title",
    subtitleLabel: "Artist",
    captionLabel: "Genre",
  },
];
const DEFAULT_DESCRIPTION_TYPE_OPTIONS = ["Description", "Lyrics"];
const DEFAULT_SOURCE_OPTIONS = [
  {
    label: "WWG Exclusive",
    value: "MEDIA_SOURCE_WWG_EXCLUSIVE",
    isThirdParty: false,
  },
  {
    label: "3rd Party",
    value: "MEDIA_SOURCE_3RD_PARTY",
    isThirdParty: true,
  },
];
const NOOP = () => {};

const TEXT_FIELDS = [
  { name: "releaseOn", label: "Release Date", compact: true },
  { name: "sku", label: "SKU", compact: true },
  { name: "title" },
  { name: "creatorSpeaker" },
  { name: "topic" },
];

export function AudioDetailsForm({
  mediaType,
  mediaTypeOptions,
  sourceCode,
  sourceOptions,
  contentType,
  contentTypeOptions,
  mediaTemplates,
  descriptionType,
  descriptionValue,
  year,
  releaseOn,
  publishOn,
  sku,
  title,
  creatorSpeaker,
  topic,
  naCrNumber,
  anzCrNumber,
  isThirdParty,
  onChange,
  className,
  errors = {},
  skuDuplicateError = false,
}) {
  // Determine effective release date: use releaseOn if provided, otherwise publishOn (for create/edit)
  const effectiveReleaseOn = releaseOn || publishOn;

  // Local state to track user's formatted date input while typing
  const [yearInputValue, setYearInputValue] = React.useState(
    year ? formatDate(year, "M.D.YYYY") : "",
  );

  // Local state to track user's formatted release date input while typing
  const [releaseOnInputValue, setReleaseOnInputValue] = React.useState(
    effectiveReleaseOn ? formatISODatetimeToDisplay(effectiveReleaseOn) : "",
  );

  // Ref to maintain cursor position when formatting the input
  const releaseOnInputRef = React.useRef(null);

  // Sync yearInputValue when year prop changes (e.g., loading new media)
  React.useEffect(() => {
    const formatted = year ? formatDate(year, "M.D.YYYY") : "";
    setYearInputValue(formatted);
  }, [year]);

  // Sync releaseOnInputValue when releaseOn or publishOn changes
  React.useEffect(() => {
    const formatted = effectiveReleaseOn ? formatISODatetimeToDisplay(effectiveReleaseOn) : "";
    setReleaseOnInputValue(formatted);
  }, [effectiveReleaseOn]);

  const values = {
    mediaType,
    sourceCode,
    contentType,
    descriptionType,
    descriptionValue,
    year,
    releaseOn,
    sku,
    title,
    creatorSpeaker,
    topic,
    naCrNumber,
    anzCrNumber,
    isThirdParty,
  };

  const emitChange = (field, value) => {
    const nextValues = {
      ...values,
      [field]: value,
    };

    console.log("📋 AudioDetailsForm emitChange:", {
      field,
      value,
      previousValue: values[field],
      nextValues_field: nextValues[field],
    });

    onChange(field, value, nextValues);
  };

  const resolvedSourceOptions =
    Array.isArray(sourceOptions) && sourceOptions.length > 0
      ? sourceOptions
      : DEFAULT_SOURCE_OPTIONS;

  const sourceValueFromBoolean = Boolean(isThirdParty)
    ? resolvedSourceOptions.find((option) => option.isThirdParty)?.value
    : resolvedSourceOptions.find((option) => !option.isThirdParty)?.value;

  const activeSourceValue =
    sourceCode || sourceValueFromBoolean || resolvedSourceOptions[0]?.value;
  const resolvedMediaTemplates =
    Array.isArray(mediaTemplates) && mediaTemplates.length > 0
      ? mediaTemplates
      : DEFAULT_CONTENT_TYPE_TEMPLATES;
  const selectedTemplate =
    resolvedMediaTemplates.find(
      (template) =>
        String(template.label || "").toLowerCase() ===
        String(contentType || "").toLowerCase(),
    ) || DEFAULT_CONTENT_TYPE_TEMPLATES[0];
  const fieldLabels = {
    title: selectedTemplate.titleLabel,
    creatorSpeaker: selectedTemplate.subtitleLabel,
    topic: selectedTemplate.captionLabel,
  };

  const handleSourceSelect = (option) => {
    const nextValues = {
      ...values,
      sourceCode: option.value,
      isThirdParty: Boolean(option.isThirdParty),
    };

    onChange("sourceCode", option.value, nextValues);
  };

  const handleReleaseOnChange = (event) => {
    const userInput = event.target.value;
    const cursorPos = event.target.selectionStart; // Get cursor position before formatting
    
    // Allow editing: just remove non-numeric characters to get raw digits
    const numericOnly = userInput.replace(/\D/g, '');
    
    // Format the display value (M.DD.YYYY) - this adds dots at correct positions
    const formattedDisplay = formatReleaseDateInput(numericOnly);
    
    // Calculate new cursor position based on numeric digits
    // Count how many numeric digits were before the cursor
    let digitCountBeforeCursor = 0;
    for (let i = 0; i < cursorPos && i < userInput.length; i++) {
      if (/\d/.test(userInput[i])) {
        digitCountBeforeCursor++;
      }
    }
    
    // Find position in formatted string after the same number of digits
    let newCursorPos = 0;
    let digitCount = 0;
    for (let i = 0; i < formattedDisplay.length; i++) {
      if (/\d/.test(formattedDisplay[i])) {
        digitCount++;
        if (digitCount === digitCountBeforeCursor) {
          newCursorPos = i + 1;
          break;
        }
      }
    }
    // If we're at the end of digits, position cursor after last digit (before next dot if any)
    if (digitCountBeforeCursor === numericOnly.length) {
      newCursorPos = formattedDisplay.length;
    }
    
    // Update local state to show the formatted value
    setReleaseOnInputValue(formattedDisplay);

    // Restore cursor position after state update (using setTimeout to defer)
    setTimeout(() => {
      if (releaseOnInputRef.current) {
        releaseOnInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    // Only try to convert to ISO if we have a complete date (7+ digits = complete date)
    if (numericOnly.length >= 7) {
      const isoValue = convertFormattedDateToISO(formattedDisplay);
      
      console.log("📝 Release On Input Change:", {
        userInput,
        numericOnly,
        formattedDisplay,
        cursorPos,
        newCursorPos,
        isoValue,
        isComplete: isoValue !== null,
      });

      // Update parent if we have a valid ISO date
      if (isoValue) {
        emitChange("releaseOn", isoValue);
      }
    }
  };

  const handleYearChange = (event) => {
    const userInput = event.target.value;
    // Format the display value (MM.DD.YYYY)
    const formattedDisplay = formatReleaseDateInput(userInput);
    // Update local state so the input shows what user typed
    setYearInputValue(formattedDisplay);

    // Convert to ISO format for storage (YYYY-MM-DD)
    const isoValue = convertFormattedDateToISO(formattedDisplay);

    // Only update parent if we have a valid ISO date (complete 8 digits)
    if (isoValue) {
      emitChange("year", isoValue);
    }
  };

  return (
    <section className={classNames(styles.card, className)}>
      <div className={styles.inner}>
        <div className={styles.headerWrap}>
          <h3 className={styles.title}>Audio Details</h3>
        </div>

        <div className={styles.divider} />

        <div className={styles.mediaTypeRow}>
          <label className={styles.label} htmlFor="audio-details-media-type">
            Media Type
          </label>
          <Dropdown
            className={styles.mediaTypeDropdown}
            value={mediaType}
            options={mediaTypeOptions}
            placeholder="Select"
            onChange={(nextValue) => emitChange("mediaType", nextValue)}
            chevronClassName={styles.mediaTypeChevron}
            chevronOpenClassName={styles.mediaTypeChevronUp}
            buttonBackgroundColor="#ffffff"
            menuBackgroundColor="#283b4d"
          />
        </div>

        {TEXT_FIELDS.map((field) => {
          const fieldValue = values[field.name] || "";
          const rowClassName = field.compact
            ? styles.compactRow
            : styles.longRow;

          if (field.name === "title") {
            return (
              <React.Fragment key="audio-details-content-type-and-title">
                <div className={styles.compactRow}>
                  <label
                    className={styles.label}
                    htmlFor="audio-details-content-type"
                  >
                    Content Type
                  </label>
                  <Dropdown
                    className={styles.mediaTypeDropdown}
                    value={contentType}
                    options={contentTypeOptions}
                    placeholder="Select"
                    onChange={(nextValue) => {
                      const nextValues = {
                        ...values,
                        contentType: nextValue,
                        ...(nextValue === "Music"
                          ? { mediaType: "Music" }
                          : {}),
                      };

                      onChange("contentType", nextValue, nextValues);
                    }}
                    chevronClassName={styles.mediaTypeChevron}
                    chevronOpenClassName={styles.mediaTypeChevronUp}
                    buttonBackgroundColor="#ffffff"
                    menuBackgroundColor="#283b4d"
                  />
                </div>

                <div key={field.name} className={rowClassName}>
                  <label
                    className={styles.label}
                    htmlFor={`audio-details-${field.name}`}
                  >
                    {fieldLabels[field.name]}
                    {(field.name === "title" ||
                      field.name === "creatorSpeaker" ||
                      field.name === "sku") && (
                      <span className={styles.required}> *</span>
                    )}
                  </label>
                  <input
                    id={`audio-details-${field.name}`}
                    name={field.name}
                    type="text"
                    value={fieldValue}
                    className={classNames(styles.input, {
                      [styles.inputError]: errors[field.name],
                    })}
                    placeholder={field.placeholder || ""}
                    onChange={(event) =>
                      emitChange(field.name, event.target.value)
                    }
                  />
                </div>
                {errors[field.name] && (
                  <div className={styles.errorMessage}>
                    This field is required.
                  </div>
                )}
              </React.Fragment>
            );
          }

          if (field.name === "topic") {
            return (
              <React.Fragment key="audio-details-event-and-description-type">
                <div className={rowClassName}>
                  <label
                    className={styles.label}
                    htmlFor={`audio-details-${field.name}`}
                  >
                    {fieldLabels[field.name]}
                    <span className={styles.required}> *</span>
                  </label>
                  <input
                    id={`audio-details-${field.name}`}
                    name={field.name}
                    type="text"
                    value={fieldValue}
                    className={classNames(styles.input, {
                      [styles.inputError]: errors[field.name],
                    })}
                    placeholder={field.placeholder || ""}
                    onChange={(event) =>
                      emitChange(field.name, event.target.value)
                    }
                  />
                </div>
                {errors[field.name] && (
                  <div className={styles.errorMessage}>
                    This field is required.
                  </div>
                )}

                <div className={styles.descriptionTypeBlock}>
                  <label
                    className={styles.label}
                    htmlFor="audio-details-description-value"
                  >
                    Description Type
                  </label>
                  <div className={styles.descriptionTypeOptions}>
                    {DEFAULT_DESCRIPTION_TYPE_OPTIONS.map((option) => {
                      const isSelected =
                        String(values.descriptionType || "").toLowerCase() ===
                        option.toLowerCase();

                      return (
                        <button
                          key={option}
                          type="button"
                          className={classNames(styles.descriptionTypeOption, {
                            [styles.descriptionTypeOptionSelected]: isSelected,
                          })}
                          onClick={() => emitChange("descriptionType", option)}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  {/* Multi-line so pasted or typed line breaks (lyrics) are
                      kept; they're stored as-is and the apps render them. */}
                  <textarea
                    id="audio-details-description-value"
                    name="descriptionValue"
                    value={values.descriptionValue || ""}
                    className={classNames(styles.input, styles.textarea)}
                    rows={
                      String(values.descriptionType || "").toLowerCase() ===
                      "lyrics"
                        ? 10
                        : 4
                    }
                    placeholder=""
                    onChange={(event) =>
                      emitChange("descriptionValue", event.target.value)
                    }
                  />
                </div>
              </React.Fragment>
            );
          }

          const displayValue =
            field.name === "releaseOn" ? releaseOnInputValue : fieldValue;

          return (
            <React.Fragment key={field.name}>
              <div className={rowClassName}>
                <label
                  className={styles.label}
                  htmlFor={`audio-details-${field.name}`}
                >
                  {field.label || fieldLabels[field.name]}
                  {(field.name === "sku" ||
                    field.name === "creatorSpeaker") && (
                    <span className={styles.required}> *</span>
                  )}
                </label>
                <input
                  ref={field.name === "releaseOn" ? releaseOnInputRef : null}
                  id={`audio-details-${field.name}`}
                  name={field.name}
                  type="text"
                  value={displayValue}
                  className={classNames(styles.input, {
                    [styles.inputError]:
                      errors[field.name] ||
                      (field.name === "sku" && skuDuplicateError),
                  })}
                  placeholder={field.placeholder || ""}
                  onChange={(event) => {
                    if (field.name === "releaseOn") {
                      handleReleaseOnChange(event);
                      return;
                    }

                    if (field.name === "year") {
                      handleYearChange(event);
                      return;
                    }

                    emitChange(
                      field.name,
                      field.name === "sku"
                        ? sanitizeSku(event.target.value)
                        : event.target.value,
                    );
                  }}
                />
              </div>
              {errors[field.name] && (
                <div
                  className={classNames(styles.errorMessage, {
                    [styles.errorMessageSkuAligned]: field.name === "sku",
                  })}
                >
                  This field is required.
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className={styles.thirdPartyRow}>
          <span className={styles.label}>Source</span>
          <div
            className={styles.sourceOptions}
            role="radiogroup"
            aria-label="Source"
          >
            {resolvedSourceOptions.map((option) => {
              const checked = activeSourceValue === option.value;

              return (
                <button
                  key={option.label}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  className={styles.sourceOption}
                  onClick={() => handleSourceSelect(option)}
                >
                  <ImageRadioOption
                    checked={checked}
                    label={option.label}
                    className={styles.sourceOptionContent}
                    iconClassName={styles.sourceRadioIcon}
                    labelClassName={styles.sourceOptionLabel}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

AudioDetailsForm.propTypes = {
  mediaType: PropTypes.oneOf(["Audio", "Video", "Music"]),
  mediaTypeOptions: PropTypes.arrayOf(PropTypes.string),
  sourceCode: PropTypes.string,
  sourceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isThirdParty: PropTypes.bool,
    }),
  ),
  contentType: PropTypes.oneOf(["Event", "Bio", "Music"]),
  contentTypeOptions: PropTypes.arrayOf(PropTypes.string),
  mediaTemplates: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      titleLabel: PropTypes.string.isRequired,
      subtitleLabel: PropTypes.string.isRequired,
      captionLabel: PropTypes.string.isRequired,
    }),
  ),
  descriptionType: PropTypes.oneOf(["Description", "Lyrics"]),
  descriptionValue: PropTypes.string,
  year: PropTypes.string,
  releaseOn: PropTypes.string,
  publishOn: PropTypes.string,
  sku: PropTypes.string,
  title: PropTypes.string,
  creatorSpeaker: PropTypes.string,
  topic: PropTypes.string,
  naCrNumber: PropTypes.string,
  anzCrNumber: PropTypes.string,
  isThirdParty: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  errors: PropTypes.object,
  skuDuplicateError: PropTypes.bool,
};

AudioDetailsForm.defaultProps = {
  mediaType: "Audio",
  mediaTypeOptions: DEFAULT_MEDIA_OPTIONS,
  sourceCode: "",
  sourceOptions: DEFAULT_SOURCE_OPTIONS,
  contentType: "Event",
  contentTypeOptions: DEFAULT_CONTENT_TYPE_OPTIONS,
  mediaTemplates: DEFAULT_CONTENT_TYPE_TEMPLATES,
  descriptionType: "Description",
  descriptionValue: "",
  year: "",
  sku: "",
  title: "",
  creatorSpeaker: "",
  topic: "",
  naCrNumber: "",
  anzCrNumber: "",
  isThirdParty: false,
  onChange: NOOP,
  className: undefined,
  errors: {},
  skuDuplicateError: false,
};

export default AudioDetailsForm;
